import { Fragment, h } from "preact";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  Cell,
} from "@table-library/react-table-library/table";
import { useTheme } from "@table-library/react-table-library/theme";
import sortBy from "lodash/sortBy";

import i18n from "../../i18n";
import {
  downloadFile,
  stringTofileName,
  talentStateIsChoosable,
} from "../../helpers";
import {
  markNotificationAsRead,
  selectNotifications,
} from "../../store/notificationsSlice";
import notificationsApi from "../../api/notifications";

import Tag from "../Tag.jsx";
import TalentAction from "../TalentAction.jsx";
import CountryFlag from "../CountryFlag.jsx";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import Avatar from "../Avatar.jsx";
import Icon from "../Icon.jsx";
import Dropdown from "../Dropdown.jsx";
import ShareProfileDialog from "../ShareProfileDialog.jsx";
import NotificationBubble from "../NotificationBubble.jsx";
import { sendError } from "../../errors.js";
import DefineStateDialog from "../DefineStateDialog.jsx";
import AppliedIcon from "../AppliedIcon.jsx";

export default function TalentTable({
  project,
  onDeleteFromProject,
  talents,
  onUpdateTalentState,
}) {
  const wrapperRef = useRef();
  const tableRef = useRef();
  const startIndicatorRef = useRef();
  const endIndicatorRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = {
    nodes: sortBy(talents, (talent) => -talent.match),
  };
  const [isAtBeginning, setIsAtBeginning] = useState(false);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [openedShareDialog, setOpenedShareDialog] = useState(null);
  const [openedDefineStateDialog, setOpenedDefineStateDialog] = useState(null);
  const [prepared, setPrepared] = useState(false);
  const [tableWidth, setTableWidth] = useState(0);

  const notifications = useSelector(selectNotifications);
  const needsAppliedColumn = talents.some(
    (talent) => talent.projects.length > 0
  );

  const isMobile = window.innerWidth < 768;

  const theme = useTheme({
    Table: needsAppliedColumn
      ? `
        --data-table-library_grid-template-columns: auto auto auto auto auto auto auto auto auto auto auto auto auto auto auto;
      `
      : `
      --data-table-library_grid-template-columns: auto auto auto auto auto auto auto auto auto auto auto auto auto auto;
    `,
    Cell: isMobile
      ? ""
      : `
      &:nth-of-type(1) {
        left: 0px;
      }

      &:nth-of-type(${needsAppliedColumn ? "15" : "14"}) {
        right: 0px;
      }
    `,
  });

  function checkScrolling() {
    setIsAtBeginning(tableRef.current.scrollLeft == 0);
    setIsAtEnd(
      tableRef.current.scrollLeftMax - tableRef.current.scrollLeft == 0
    );
  }

  function onDefineStateClick(talent) {
    setOpenedDefineStateDialog(talent);
  }

  async function markTalentAsRead(talent) {
    try {
      const notification = notifications.find((n) => n.targetId === talent.id);
      if (notification) {
        await notificationsApi.markAsRead(
          notification.targetId,
          "projects.talent_added"
        );
        dispatch(markNotificationAsRead(notification));
      }
    } catch (err) {
      console.error(err);
      sendError("Could not mark project talent as read.", {
        talentId: talent.id,
        pathname: window.location.pathname,
      });
    }
  }

  useLayoutEffect(() => {
    if (!prepared || isMobile) {
      return;
    }
    if (data.nodes.length) {
      setTimeout(() => {
        const startWidth = tableRef.current
          .querySelector("td:first-child")
          .getBoundingClientRect().width;
        const endWidth = tableRef.current
          .querySelector("td:last-child")
          .getBoundingClientRect().width;
        if (startIndicatorRef.current) {
          startIndicatorRef.current.style.left = `${startWidth}px`;
        }
        if (endIndicatorRef.current) {
          endIndicatorRef.current.style.right = `${endWidth}px`;
        }
      }, 100);
    }
  }, [data, prepared]);

  useLayoutEffect(() => {
    if (!prepared) {
      setTableWidth(wrapperRef.current.getBoundingClientRect().width);
      setPrepared(true);
    }
  }, []);

  return (
    <div class="talent-table__wrapper" ref={wrapperRef}>
      <Table
        className="talent-table"
        data={data}
        theme={theme}
        layout={{ custom: true, horizontalScroll: true }}
        onScroll={checkScrolling}
        ref={tableRef}
        style={{
          display: prepared ? "grid" : "hidden",
          width: `${tableWidth}px`,
        }}
      >
        {(tableList) => {
          return (
            <>
              <Header>
                <HeaderRow className="talent-table__row">
                  <Cell className="talent-table__header-cell" pinLeft>
                    {i18n.t("pages.projects.talent_table.header.name")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.job_title")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.company")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.match")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.seniority")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.experience")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.top_skills")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.salary")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.languages")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.notice_period")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.residence")}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t(
                      "pages.projects.talent_table.header.preferred_worktype"
                    )}
                  </Cell>
                  <Cell className="talent-table__header-cell">
                    {i18n.t("pages.projects.talent_table.header.added_by")}
                  </Cell>
                  {needsAppliedColumn && (
                    <Cell className="talent-table__header-cell">
                      {i18n.t("pages.projects.talent_table.header.applied")}
                    </Cell>
                  )}
                  <Cell className="talent-table__header-cell" pinRight>
                    {i18n.t("pages.projects.talent_table.header.action")}
                  </Cell>
                </HeaderRow>
              </Header>

              <Body>
                {tableList.map((talent) => {
                  let salaryDiff = "-";
                  if (talent.salaryDifference === 0) {
                    salaryDiff = (
                      <Icon className="color-semantic-success">checkmark</Icon>
                    );
                  } else if (talent.salaryDifference > 0) {
                    salaryDiff = (
                      <span className="color-semantic-error inline-flex-center">
                        <Icon>money_up</Icon>{" "}
                        {Math.round(talent.salaryDifference)} %
                      </span>
                    );
                  }

                  function clickOnTalent(event) {
                    event && event.preventDefault();
                    markTalentAsRead(talent);
                    navigate(
                      `/projects/${project.id}/talents/${talent.talent.id}`
                    );
                  }

                  const dropdownOptions = [
                    {
                      trackingName: "Show profile",
                      icon: "show_profile",
                      text: i18n.t(
                        "pages.projects.talent_table.actions.show_profile"
                      ),
                      onClick: clickOnTalent,
                    },
                    talentStateIsChoosable(talent.talent) && {
                      trackingName: "Define state",
                      icon: "define_state",
                      text: i18n.t(
                        "pages.projects.talent_table.actions.define_state"
                      ),
                      onClick: function () {
                        onDefineStateClick(talent.talent);
                      },
                    },
                    {
                      trackingName: "Remove from project",
                      icon: "folder_new",
                      text: i18n.t(
                        "pages.projects.talent_table.actions.remove"
                      ),
                      onClick: function () {
                        onDeleteFromProject(talent.talent);
                      },
                    },
                    {
                      trackingName: "Share profile",
                      icon: "share_new",
                      onClick: function () {
                        setOpenedShareDialog(talent.talent);
                      },
                      text: i18n.t("pages.projects.talent_table.actions.share"),
                    },
                    {
                      trackingName: "Download resume",
                      icon: "download_new",
                      onClick: function () {
                        downloadFile(
                          talent.resume.url,
                          `resume_${stringTofileName(talent.name)}`
                        );
                      },
                      text: i18n.t(
                        "pages.projects.talent_table.actions.download"
                      ),
                    },
                  ];
                  return (
                    <Row
                      className="talent-table__row"
                      key={talent.talent.id}
                      talent={talent}
                    >
                      <Cell className="talent-table__cell" pinLeft>
                        <NotificationBubble
                          class="mr-8"
                          notification={notifications.find(
                            (n) => n.targetId === talent.id
                          )}
                        />
                        <Link
                          className="link--invisible"
                          to={`/projects/${project.id}/talents/${talent.talent.id}`}
                          onClick={clickOnTalent}
                        >
                          <Avatar plain small user={talent} class="mr-8" />
                          {talent.name}
                        </Link>
                      </Cell>
                      <Cell className="talent-table__cell">
                        {talent.jobTitle || "-"}
                      </Cell>
                      <Cell className="talent-table__cell">
                        {talent.company || "-"}
                      </Cell>
                      <Cell className="talent-table__cell talent-table__cell--centered">
                        <Tag
                          grey
                          noHover
                          small
                          class={`mb-0 tag--skill-${
                            Math.round(talent.match / 10) * 10
                          }`}
                        >
                          {talent.match} %
                        </Tag>
                      </Cell>
                      <Cell className="talent-table__cell">
                        {i18n.t(`options.seniority_string.${talent.seniority}`)}
                      </Cell>
                      <Cell className="talent-table__cell">
                        {talent.yearsOfWorkExperience
                          ? i18n.t("date.years", {
                              count: talent.yearsOfWorkExperience,
                            })
                          : ""}
                      </Cell>
                      <Cell className="talent-table__cell">
                        {talent.topSkills.sort().join(", ") || "-"}
                      </Cell>
                      <Cell className="talent-table__cell">{salaryDiff}</Cell>
                      <Cell className="talent-table__cell">
                        {talent.languages.map((lang) => (
                          <Tag grey small class="mb-0">
                            {lang}
                          </Tag>
                        ))}
                      </Cell>
                      <Cell className="talent-table__cell">
                        {talent.noticePeriod
                          ? i18n.t("date.months", {
                              count: talent.noticePeriod,
                            })
                          : "-"}
                      </Cell>
                      <Cell className="talent-table__cell talent-table__cell--centered">
                        <CountryFlag code={talent.countryCode} />
                      </Cell>
                      <Cell className="talent-table__cell">
                        {talent.preferredWorktype || "-"}
                      </Cell>
                      <Cell className="talent-table__cell talent-table__cell--centered">
                        {talent.addedBy ? (
                          <Avatar plain small user={talent.addedBy} />
                        ) : (
                          <img
                            src="/images/logo_icon_midnight.svg"
                            width="24"
                            height="24"
                          />
                        )}
                      </Cell>
                      {needsAppliedColumn && (
                        <Cell className="talent-table__cell talent-table__cell--centered">
                          <AppliedIcon user={talent} />
                        </Cell>
                      )}
                      <Cell
                        className="talent-table__cell  talent-table__cell--action"
                        pinRight
                      >
                        <TalentAction
                          text
                          talent={talent.talent}
                          small
                          onChange={onUpdateTalentState}
                        />
                        <Dropdown
                          items={dropdownOptions}
                          absolute={true}
                          container={document.getElementById("modals")}
                        >
                          <Icon>more</Icon>
                        </Dropdown>
                      </Cell>
                    </Row>
                  );
                })}
              </Body>
            </>
          );
        }}
      </Table>
      {!isAtBeginning && (
        <div
          class="talent-table__scroll-indicator talent-table__scroll-indicator--start"
          ref={startIndicatorRef}
        ></div>
      )}
      {!isAtEnd && (
        <div
          class="talent-table__scroll-indicator talent-table__scroll-indicator--end"
          ref={endIndicatorRef}
        ></div>
      )}
      {openedShareDialog && (
        <ShareProfileDialog
          onClose={() => setOpenedShareDialog(null)}
          talent={openedShareDialog}
        />
      )}
      {openedDefineStateDialog && (
        <DefineStateDialog
          onClose={() => setOpenedDefineStateDialog(null)}
          talent={openedDefineStateDialog}
        />
      )}
    </div>
  );
}
